import React, { useState } from 'react';
import { Maximize2, Paintbrush, ChevronDown, RotateCcw } from 'lucide-react';
import { fontOptions, isLightColor } from '../../themeDefaults';
import {
  HEADING_SIZE_OPTIONS,
  ALIGNMENT_OPTIONS,
  BUTTON_STYLE_OPTIONS,
  PADDING_OPTIONS,
} from './commonDefaults';

// ─── Shared sub-components ──────────────────────────────────────────────────

/**
 * ColorPicker — Inline color swatch + hex input + reset button.
 */
const ColorPicker = ({ label, value, fallback, onChange, onReset }) => {
  const displayValue = value || fallback;

  return (
    <div>
      <label className="block text-sm text-gray-300 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-gray-700 flex-shrink-0"
        />
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none"
        />
        {value != null && (
          <button
            type="button"
            onClick={onReset}
            className="p-2 text-gray-400 hover:text-[#749555] transition-colors"
            title="Reset to theme default"
          >
            <RotateCcw size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * SegmentedButtons — Reusable row of toggle buttons (e.g. S/M/L/XL).
 */
const SegmentedButtons = ({ label, options, value, onChange, columns = options.length }) => (
  <div>
    <label className="block text-sm text-gray-300 mb-1">{label}</label>
    <div className={`grid gap-1.5`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`py-1.5 rounded text-sm font-medium transition-colors ${
            value === opt.value
              ? 'bg-[#749555] text-white'
              : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

// ─── Collapsible panel shell ────────────────────────────────────────────────

const PanelShell = ({ icon: Icon, title, children, isOpen: controlledOpen, onToggle }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const handleToggle = () => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-[#1a1a2e] hover:bg-[#1e1e3a] text-gray-300 transition-colors"
      >
        <Icon size={16} className="text-[#749555]" />
        <span className="text-sm font-medium flex-1 text-left">{title}</span>
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-3 pb-4 pt-2 bg-[#141428] space-y-5">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── SizeSpacingPanel ───────────────────────────────────────────────────────

/**
 * SizeSpacingPanel — "Size & Spacing" collapsible panel.
 *
 * Standard controls (gated by `features`):
 *   heading tier → headingSize (S/M/L/XL), headingAlignment (L/C/R)
 *   spacing tier → sectionPadding (Compact/Normal/Spacious)
 *
 * @param {object}     style          - content.style object
 * @param {function}   onChange       - (styleUpdates) => void
 * @param {string[]}   features       - which tiers to show
 * @param {ReactNode}  extraControls  - block-specific spatial controls rendered at the end
 */
export const SizeSpacingPanel = ({ style = {}, onChange, features = [], extraControls, isOpen, onToggle }) => {
  const has = (tier) => features.includes(tier);
  const handleChange = (prop, value) => onChange({ [prop]: value });

  const hasStandard = has('heading') || has('spacing');
  if (!hasStandard && !extraControls) return null;

  return (
    <PanelShell icon={Maximize2} title="Size & Spacing" isOpen={isOpen} onToggle={onToggle}>
      {/* ── Heading tier (size + alignment only) ── */}
      {has('heading') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Heading
          </h4>
          <SegmentedButtons
            label="Size"
            options={HEADING_SIZE_OPTIONS}
            value={style.headingSize}
            onChange={(v) => handleChange('headingSize', v)}
            columns={4}
          />
          <SegmentedButtons
            label="Alignment"
            options={ALIGNMENT_OPTIONS}
            value={style.headingAlignment}
            onChange={(v) => handleChange('headingAlignment', v)}
            columns={3}
          />
        </div>
      )}

      {/* ── Spacing tier ── */}
      {has('spacing') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Spacing
          </h4>
          <SegmentedButtons
            label="Section Padding"
            options={PADDING_OPTIONS}
            value={style.sectionPadding}
            onChange={(v) => handleChange('sectionPadding', v)}
            columns={3}
          />
        </div>
      )}

      {/* ── Block-specific extras ── */}
      {extraControls}
    </PanelShell>
  );
};

// ─── ColorsFontsPanel ───────────────────────────────────────────────────────

/**
 * ColorsFontsPanel — "Colors & Fonts" collapsible panel.
 *
 * Standard controls (gated by `features`):
 *   heading tier → headingFont (dropdown), headingColor (color picker)
 *   body tier    → bodyFont, bodyColor
 *   colors tier  → sectionBackground, cardBackground, accentColor, inputBackground
 *   button tier  → buttonStyle (Primary/Secondary/Outline), buttonColor
 *
 * @param {object}     style          - content.style object
 * @param {function}   onChange       - (styleUpdates) => void
 * @param {string[]}   features       - which tiers to show
 * @param {object}     theme          - resolvedTheme for reading defaults
 * @param {string}     blockType      - for conditional UI (inputBackground)
 * @param {ReactNode}  extraControls  - block-specific visual controls rendered at the end
 */
export const ColorsFontsPanel = ({ style = {}, onChange, features = [], theme = {}, blockType, extraControls, isOpen, onToggle }) => {
  const colors = theme.colors || {};
  const has = (tier) => features.includes(tier);
  const handleChange = (prop, value) => onChange({ [prop]: value });
  const handleReset = (prop) => onChange({ [prop]: null });

  const showInputBackground = has('colors') && (blockType === 'contact' || blockType === 'donations');

  const hasStandard = has('heading') || has('body') || has('colors') || has('button');
  if (!hasStandard && !extraControls) return null;

  return (
    <PanelShell icon={Paintbrush} title="Colors & Fonts" isOpen={isOpen} onToggle={onToggle}>
      {/* ── Heading tier (font + color only) ── */}
      {has('heading') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Heading
          </h4>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Font</label>
            <select
              value={style.headingFont || ''}
              onChange={(e) => handleChange('headingFont', e.target.value || null)}
              className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none"
            >
              <option value="">Theme Default</option>
              {fontOptions.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <ColorPicker
            label="Color"
            value={style.headingColor}
            fallback={colors.headingColor || '#ffffff'}
            onChange={(v) => handleChange('headingColor', v)}
            onReset={() => handleReset('headingColor')}
          />
        </div>
      )}

      {/* ── Body tier ── */}
      {has('body') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Body Text
          </h4>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Font</label>
            <select
              value={style.bodyFont || ''}
              onChange={(e) => handleChange('bodyFont', e.target.value || null)}
              className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none"
            >
              <option value="">Theme Default</option>
              {fontOptions.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <ColorPicker
            label="Color"
            value={style.bodyColor}
            fallback={colors.bodyColor || '#ffffff'}
            onChange={(v) => handleChange('bodyColor', v)}
            onReset={() => handleReset('bodyColor')}
          />
        </div>
      )}

      {/* ── Colors tier ── */}
      {has('colors') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Colors
          </h4>
          <ColorPicker
            label="Section Background"
            value={style.sectionBackground}
            fallback={colors.sectionBackground || '#1a1a2e'}
            onChange={(v) => handleChange('sectionBackground', v)}
            onReset={() => handleReset('sectionBackground')}
          />
          <ColorPicker
            label="Card Background"
            value={style.cardBackground}
            fallback={colors.cardBackground || '#1e1e2e'}
            onChange={(v) => handleChange('cardBackground', v)}
            onReset={() => handleReset('cardBackground')}
          />
          <ColorPicker
            label="Accent Color"
            value={style.accentColor}
            fallback={colors.accent || '#749555'}
            onChange={(v) => handleChange('accentColor', v)}
            onReset={() => handleReset('accentColor')}
          />
          {showInputBackground && (
            <ColorPicker
              label="Input Background"
              value={style.inputBackground}
              fallback="#252540"
              onChange={(v) => handleChange('inputBackground', v)}
              onReset={() => handleReset('inputBackground')}
            />
          )}
        </div>
      )}

      {/* ── Button tier ── */}
      {has('button') && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Button
          </h4>
          <SegmentedButtons
            label="Style"
            options={BUTTON_STYLE_OPTIONS}
            value={style.buttonStyle}
            onChange={(v) => handleChange('buttonStyle', v)}
            columns={3}
          />
          <ColorPicker
            label="Color"
            value={style.buttonColor}
            fallback={colors.accent || '#749555'}
            onChange={(v) => handleChange('buttonColor', v)}
            onReset={() => handleReset('buttonColor')}
          />
          <ColorPicker
            label="Text Color"
            value={style.buttonTextColor}
            fallback={(() => {
              const btnBg = style.buttonColor || colors.accent || '#749555';
              return isLightColor(btnBg) ? '#1a1a2e' : '#ffffff';
            })()}
            onChange={(v) => handleChange('buttonTextColor', v)}
            onReset={() => handleReset('buttonTextColor')}
          />
        </div>
      )}

      {/* ── Block-specific extras ── */}
      {extraControls}
    </PanelShell>
  );
};

// ─── Default export (backward compat) ───────────────────────────────────────

/**
 * BlockStyleSettings — Renders both panels in sequence.
 * Drop-in replacement for the original single-panel component.
 */
const BlockStyleSettings = ({ style, onChange, features, theme, blockType }) => (
  <div className="space-y-2">
    <SizeSpacingPanel style={style} onChange={onChange} features={features} />
    <ColorsFontsPanel style={style} onChange={onChange} features={features} theme={theme} blockType={blockType} />
  </div>
);

export default BlockStyleSettings;
